import { Box, Card, Stack, Typography, Button, Chip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Error } from "../Error";
import { Loading } from "../Loading";
import { apiOperation, headers } from "../../services/api";
import { ETipoStatusPagamento } from "../../models/enums/TipoStatusPagamento";
import { Produto } from "../../models/notouch/produto";
import { Pedido } from "../../models/notouch/pedido";
import { Resultado } from "../../models/base/basic";
import { useNavigate } from "react-router-dom";


interface OrderDetailsProps {
  selectedProduct?: Produto;
  selectedOrder?: Pedido;
}

export const OrderDetails: React.FC<OrderDetailsProps> = ({ selectedProduct, selectedOrder }) => {
  const [loading, setLoading] = useState({ state: false, message: "" });
  const [error, setError] = useState({ state: false, title: "", message: "" });
  const [order, setOrder] = useState<Pedido | undefined>(selectedOrder);

  const navigate = useNavigate();

  const createPedido = async () => {
    try {
      setLoading({ state: true, message: "Criando pedido..." });

      const request = { ProdutoId: selectedProduct?.id }
      const config = { headers: headers() };
      const { data } = await apiOperation.post<Resultado<Pedido>>(`notouch/pedido/inserir`, request, config);
      
      if (!data.erro) {
        setOrder(data.valor);
        setLoading({ state: false, message: "" });
      } else {
        setError({
          state: true,
          title: "Não foi possivel se conectar a API no momento.",
          message: data.erros?.join("<hr />"),
        });
        setLoading({ state: false, message: "" });
      }

    } catch (error: any) {
      setError({
        state: true,
        title: `API Não disponivel`,
        message: `Caso o problema continue informe o seguinte erro: ${error?.response?.status}-${error?.response?.statusText}`,
      });
      setLoading({ state: false, message: "" });
    }
  };

  const processPagamento = async () => {
    try {
      setLoading({ state: true, message: "Verificando pagamento..." });

      const request = { Id: order?.id }
      const config = { headers: headers() };
      const { data } = await apiOperation.put<Resultado<Pedido>>(`notouch/pedido/processar`, request, config);
      
      if (!data.erro) {
        setOrder(data.valor);
        setLoading({ state: false, message: "" });

        return data.valor.pagamentoStatus;
      } else {
        setError({
          state: true,
          title: "Não foi possivel se conectar a API no momento.",
          message: data.erros?.join("<hr />"),
        });
        setLoading({ state: false, message: "" });
      }

    } catch (error: any) {
      setError({
        state: true,
        title: `API Não disponivel`,
        message: `Caso o problema continue informe o seguinte erro: ${error?.response?.status}-${error?.response?.statusText}`,
      });
      setLoading({ state: false, message: "" });
    }
  };

  const formatStatusPagamento = () => {
        var status = ETipoStatusPagamento[order?.pagamentoStatus as keyof typeof ETipoStatusPagamento];
        var css = {
      backgroundColor: "var(--action-disabled-surface)",
      color: "var(--text-on-inverse)",
      fontWeight: 500,
      px: 1,
    };

    switch (status) {
      case ETipoStatusPagamento.PAID:
      case ETipoStatusPagamento.PARTIALLY_PAID:
      case ETipoStatusPagamento.PRE_PAID:
        return { ...css, backgroundColor: "var(--success-surface)", color: "var(--text-primary)" };
      case ETipoStatusPagamento.CANCELED:
      case ETipoStatusPagamento.PARTIALLY_CANCELED:
      case ETipoStatusPagamento.DENIED:
      case ETipoStatusPagamento.EXPIRED:
        return { ...css, backgroundColor: "var(--error-surface)", color: "var(--text-primary)" };
      case ETipoStatusPagamento.PENDING:
      case ETipoStatusPagamento.CREATED:
        return { ...css, backgroundColor: "var(--warning-surface)", color: "var(--text-primary)" };
      default:
        return { ...css };
    }
      }

  const formatButtonText = () => {
    if (order) {
      var status = ETipoStatusPagamento[order.pagamentoStatus as keyof typeof ETipoStatusPagamento];

      switch (status) {
        case ETipoStatusPagamento.PENDING:
          return "Realizar Pagamento";  
        case ETipoStatusPagamento.PAID:
          return "Pedido Pago";
        case ETipoStatusPagamento.CANCELED:
        case ETipoStatusPagamento.EXPIRED:
          return "Pedido Cancelado";}
      }

    return "Finalizar Pedido";
  }

  const handleButtonClick = async () => {
    if (!order){
      return await createPedido();
    }

    window.open(order?.pagamentoURL, '_blank');

    if(!selectedOrder){
      navigate(`/historico`);
    }
  }

  useEffect(() => {
    if (selectedOrder && ETipoStatusPagamento[selectedOrder.pagamentoStatus as keyof typeof ETipoStatusPagamento] == ETipoStatusPagamento.PENDING) {
      processPagamento();
    }
  }, []);

  return (
    <>
      <Stack spacing={3} sx={{ width: '100%' }}>
        <Card sx={{ 
          boxShadow: 3, 
          p: { xs: 2, md: 3 },
          backgroundColor: "var(--surface-sunken)",
          borderRadius: 3
        }}>
          <Box sx={{
            display: "flex",
            flexDirection: { xs: "column", xl: "row" },
            alignItems: "stretch",
            gap: { xs: 2, md: 3 }
          }}>
            <Box sx={{
              flex: 1,
              minHeight: { xs: 'auto', md: 220 },
              p: { xs: 2, md: 4 },
              borderRadius: 2,
              boxShadow: 1,
              background: "linear-gradient(to bottom right, var(--surface-raised), var(--surface-sunken))",
              border: "1px solid var(--border-default)"
            }}>
              <Typography variant="h4" component="div" sx={{ 
                color: "var(--text-primary)",
                mb: 2,
                fontWeight: 600,
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}>
                {order ? order.produtoNome : selectedProduct?.nome}	
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ 
                lineHeight: 1.6,
                fontSize: { xs: '0.875rem', md: '1rem' }
              }}>
                {order ? order.produtoDescricao : selectedProduct?.descricao}
              </Typography>
            </Box>
            
            <Box sx={{
              flex: { xs: 1, xl: 0.4 },
              minHeight: { xs: 'auto', md: 220 },
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow: 1,
              background: "linear-gradient(to bottom right, var(--surface-raised), var(--surface-sunken))",
              border: "1px solid var(--border-default)",
              display: "flex",
              flexDirection: "column"
            }}>
              <Typography variant="h6" sx={{ 
                mb: 2,
                color: "var(--text-primary)",
                fontWeight: 600,
                borderBottom: "1px solid var(--border-default)",
                pb: 1,
                fontSize: { xs: '1.125rem', md: '1.25rem' }
              }}>
                Resumo do Pedido
              </Typography>
              
              <Box sx={{ 
                flex: 1, 
                display: "flex", 
                flexDirection: "column", 
                justifyContent: "space-between",
                gap: 2
              }}>
                <Box>
                  <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2
                  }}>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      Valor:
                    </Typography>
                    <Typography variant="subtitle1" sx={{ 
                      color: "var(--text-primary)",
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}>
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(order ? order.produtoPreco : selectedProduct?.preco ?? 0)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2
                  }}>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      Vigência:
                    </Typography>
                    <Typography variant="subtitle1" sx={{ 
                      color: "var(--text-primary)",
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}>
                      {(order ? order.produtoAssinatura : selectedProduct?.assinatura) ? 
                        `${order?.produtoVigencia ?? selectedProduct?.vigencia} Meses` : 
                        "Sem Vigência"}
                    </Typography>
                  </Box>
          
                  {order && (
                    <Box sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3
                    }}>
                      <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                        Pagamento:
                      </Typography>
                      <Chip 
                        label={ETipoStatusPagamento[order?.pagamentoStatus as keyof typeof ETipoStatusPagamento] ?? "N/D"} 
                        sx={formatStatusPagamento}
                        size={window.innerWidth < 600 ? "small" : "medium"}
                      />
                    </Box>
                  )}
                </Box>
                
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{
                    py: 1.5,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    fontWeight: 600,
                    borderRadius: 1,
                    boxShadow: 0,
                    textTransform: "none",
                    '&:hover': {
                      boxShadow: 2
                    },
                    mt: { xs: 1, md: 0 }
                  }}
                  disabled={order && ETipoStatusPagamento[order?.pagamentoStatus as keyof typeof ETipoStatusPagamento] != ETipoStatusPagamento.PENDING}
                  onClick={async () => await handleButtonClick()}
                >
                  {formatButtonText()}
                </Button>
              </Box>
            </Box>
          </Box>
        </Card>
      </Stack>
      <Loading
        mode="blurry"
        hidden={!loading.state}
        messages={loading.message}
      />
      <Error
        hasOpen={error.state}
        handleClose={() => setError({ state: false, title: "", message: "" })}
        errorMsg={error.message}
        title={error.title}
      />
    </>
  );
};